"use client";

import { displayDepositCents, formatPounds } from "@/lib/deposit-display";
import { pointsDiscountCents, pointsNeededForDiscount } from "@book4/utils";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

/**
 * Booking form – POSTs to backend /book.
 * Uses component factory pattern for business-type-specific fields.
 *
 * Service-based businesses with staff selection follow a stepped flow:
 *   Service → Location (if multi-location) → Professional → Date/Time → Customer Info → Confirm
 *
 * Property rentals keep the date-range flow.
 * Other business types without staff selection use the original single-page flow.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { getApiUrl, getCurrentCustomer, getCustomerToken, clearCustomerToken, type CustomerProfile } from "@/lib/api";
import { clearToken } from "@/lib/auth";
import { BusinessType, getBookingFormConfig } from "@/lib/booking-form-factory";
import { DatePickerField } from "@/components/DatePickerField";
import { BookingDateCalendar } from "@/components/BookingDateCalendar";
import { StaffSelector } from "@/components/booking-fields/StaffSelector";
import { LocationSelector } from "@/components/booking-fields/LocationSelector";
import { IntakeForm } from "@/components/booking-fields/IntakeForm";
import { PartySizeField } from "@/components/booking-fields/PartySizeField";
import { DietaryRestrictionsField } from "@/components/booking-fields/DietaryRestrictionsField";
import { PaymentForm } from "@/components/PaymentForm";
import { Loader2 } from "lucide-react";
import { useCart, type CartItem } from "./CartContext";
import { fsElement } from "@/lib/fs";
import { nightlyPriceLabel, useStayQuote } from "@/lib/stay-quote";
import { PropertyStayPrice } from "./PropertyStayPrice";

type Service = {
  id: string;
  name: string;
  description?: string | null;
  address?: string | null;
  durationMinutes: number;
  priceCents: number;
  depositCents?: number | null;
  /** Property rentals: lowest nightly price from today (base or a cheaper season), for "from £X/night" */
  fromNightlyCents?: number | null;
  images?: { id: string; url: string }[];
};
type Slot = { id: string; dayOfWeek: number; startTime: string; endTime: string; specificDate: string | null; isOff: boolean };
type LocationType = { id: string; name: string; address?: string | null; phone?: string | null };
type StaffMember = { id: string; name: string; title?: string | null; avatarUrl?: string | null; locationId?: string | null; serviceIds?: string[]; portfolioImages?: { id: string; url: string }[] };

type Props = {
  businessId: string;
  businessName: string;
  businessSlug?: string;
  businessType: BusinessType;
  featureFlags: {
    requiresStaffSelection?: boolean;
    allowsGroupBookings?: boolean;
    requiresIntakeForm?: boolean;
  };
  services: Service[];
  availability: Slot[];
  locations?: LocationType[];
  isDemo?: boolean;
  /** Whether this business can accept online payments via Stripe */
  acceptsPayments?: boolean;
  /** When true, customer can choose "Pay with cash when you arrive" instead of paying by card now. */
  acceptCashPayments?: boolean;
  /** Time slot display window in minutes on the booking page (15, 30, 60, 90, 120). Default 60. */
  bookingSlotDisplayMinutes?: number;
  /** Called when property/service selection changes (for property rental image grid). */
  onServiceIdChange?: (serviceId: string) => void;
  /** When set, pre-selects this service (e.g. from Services & Pricing list). */
  initialServiceId?: string;
  /** Called when a demo booking is successfully submitted. */
  onDemoSuccess?: () => void;
  /** Driving instructor: report email so parent can show/fetch "your packages" and allow selecting one. */
  onCustomerEmailChange?: (email: string) => void;
  /** Driving instructor: list of customer's package purchases (when parent controls it). */
  publicPackagePurchases?: Array<{ id: string; lessonPackageName: string; lessonsRemaining: number }>;
  /** Driving instructor: selected purchase id for "pay with package" (controlled). */
  selectedPublicPackageId?: string;
  /** Driving instructor: called when user selects a different package. */
  onSelectedPublicPackageIdChange?: (purchaseId: string) => void;
  /** Driving instructor: pre-fill email when set (e.g. from package card). */
  initialEmail?: string;
  /** When true, page loads for SEO but booking is disabled (e.g. trial expired). */
  bookingDisabled?: boolean;
  /** Loyalty program config (when enabled). */
  loyaltyProgram?: {
    pointsEnabled: boolean;
    pointsToPence: number;
    stampsEnabled: boolean;
    stampsPerVisit: number;
    stampsRequired: number;
    stampRewardType: string;
    stampRewardValue: number | null;
    stampRewardService?: { id: string; name: string; priceCents: number } | null;
  } | null;
  /** Called when entering (true) or leaving (false) the card payment step; used to disable add-ons during payment. */
  onPaymentStepChange?: (onStep: boolean) => void;
  /** Cancellation / refund policy from business settings (shown at checkout). */
  customerCancellationMode?: string | null;
  cancellationFullRefundDays?: number | null;
  cancellationPartialRefundPercent?: number | null;
  cancellationPartialRefundDays?: number | null;
  /** Shop hides its full address: addresses are already town and district only, so no map links. */
  addressHidden?: boolean;
};

/** Business types that ALWAYS use the stepped service flow (inherently staff/instructor-based) */
const ALWAYS_STEPPED_TYPES: BusinessType[] = [
  "HAIR_SALON",
  "BARBER_SHOP",
  "BEAUTY_SPA",
  "NAILS",
  "DRIVING_INSTRUCTOR",
];

/** Additional business types that use the stepped flow when requiresStaffSelection is enabled */
const OPTIONAL_STEPPED_TYPES: BusinessType[] = [
  "MEDICAL",
  "FITNESS",
  "SWIM_SCHOOL",
  "GENERAL",
  "PROFESSIONAL",
  "EDUCATION",
];

type BookingStep = "service" | "location" | "professional" | "datetime" | "details" | "confirm";

export function BookingForm({
  businessId,
  businessName,
  businessSlug,
  businessType,
  featureFlags,
  services,
  availability,
  locations = [],
  isDemo = false,
  acceptsPayments = false,
  acceptCashPayments = false,
  bookingSlotDisplayMinutes = 60,
  onServiceIdChange,
  initialServiceId,
  onDemoSuccess,
  onCustomerEmailChange,
  publicPackagePurchases: publicPackagePurchasesProp,
  selectedPublicPackageId: selectedPublicPackageIdProp,
  onSelectedPublicPackageIdChange,
  initialEmail,
  bookingDisabled = false,
  loyaltyProgram,
  onPaymentStepChange,
  customerCancellationMode = null,
  cancellationFullRefundDays = null,
  cancellationPartialRefundPercent = null,
  cancellationPartialRefundDays = null,
  addressHidden = false,
}: Props) {
  // Core booking data
  const [serviceId, setServiceId] = useState(services[0]?.id ?? "");
  const [locationId, setLocationId] = useState("");
  const [staffId, setStaffId] = useState(""); // staff id or "any"
  const [date, setDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);

  // Property rental: check-in / check-out date range (no time slots)
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");

  // Type-specific fields
  const [intakeData, setIntakeData] = useState<{ symptoms?: string; medicalHistory?: string; medications?: string; allergies?: string }>({});
  const [partySize, setPartySize] = useState<number>(1);
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");

  // UI state
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  /** Prevents slot-fetch success from clearing a submit error (effect can run after submit fails). */
  const errorFromSubmitRef = useRef(false);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  /** When API returns displaySlots (e.g. hourly), use these for the time picker; each has time, remaining, firstAvailableAt */
  const [displaySlots, setDisplaySlots] = useState<{ time: string; remaining: number; firstAvailableAt: string }[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [unavailableRanges, setUnavailableRanges] = useState<{ startDate: string; endDate: string }[]>([]);
  const [suggestedRanges, setSuggestedRanges] = useState<{ startDate: string; endDate: string }[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(false);

  // Payment state
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [showPayment, setShowPayment] = useState(false);
  const submitPayWithCashRef = useRef(false);
  const [lastBookingId, setLastBookingId] = useState<string | null>(null);
  const [lastPayToken, setLastPayToken] = useState<string | null>(null);
  const [payLaterLink, setPayLaterLink] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  /** Add-ons that were on the booking when it was confirmed (so we can show them after cart is cleared) */
  const [confirmedAddons, setConfirmedAddons] = useState<CartItem[]>([]);

  // Loyalty redemption (points or stamps)
  const [loyaltyRedemption, setLoyaltyRedemption] = useState<{ type: "points" | "stamps"; pointsToRedeem?: number } | null>(null);
  const [customerLoyalty, setCustomerLoyalty] = useState<{ points: number; stamps: number; emailVerified: boolean } | null>(null);

  // Public "use lesson package" (driving instructor – fetch by email). Can be controlled by parent.
  const [publicPackagePurchasesInternal, setPublicPackagePurchasesInternal] = useState<Array<{ id: string; lessonPackageName: string; lessonsRemaining: number }>>([]);
  const [selectedPublicPackageIdInternal, setSelectedPublicPackageIdInternal] = useState("");
  const publicPackagePurchases = publicPackagePurchasesProp ?? publicPackagePurchasesInternal;
  const selectedPublicPackageId = selectedPublicPackageIdProp ?? selectedPublicPackageIdInternal;
  const setSelectedPublicPackageId = onSelectedPublicPackageIdChange ?? setSelectedPublicPackageIdInternal;

  // Step tracking for service-flow businesses
  const [currentStep, setCurrentStep] = useState<BookingStep>("service");

  const pathname = usePathname();
  const router = useRouter();
  const { items: cartItems, clearCart } = useCart();
  const returnTo = pathname ? `${pathname}` : "";
  const selectedService = services.find((s) => s.id === serviceId);

  // Helper function to filter out past times for today
  const filterPastTimes = useCallback((slots: string[]): string[] => {
    if (!date) return slots;
    const today = new Date().toISOString().slice(0, 10);
    if (date !== today) return slots;
    
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    return slots.filter((time) => {
      // Compare time strings (HH:MM format)
      return time >= currentTime;
    });
  }, [date]);

  // Helper function to filter display slots (with remaining count)
  const filterPastDisplaySlots = useCallback((
    slots: { time: string; remaining: number; firstAvailableAt: string }[]
  ): { time: string; remaining: number; firstAvailableAt: string }[] => {
    if (!date) return slots;
    const today = new Date().toISOString().slice(0, 10);
    if (date !== today) return slots;
    
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    return slots.filter((ds) => {
      // Compare the display time (HH:MM format)
      return ds.time >= currentTime;
    });
  }, [date]);

  const BOOKING_DRAFT_KEY = (id: string) => `book4_booking_draft_${id}`;
  function saveBookingDraft() {
    try {
      const draft = {
        serviceId,
        locationId,
        staffId,
        date,
        selectedTime,
        checkInDate,
        checkOutDate,
        currentStep,
        email,
        name,
        phone,
        notes,
        marketingOptIn,
        intakeData,
        partySize,
        dietaryRestrictions,
        pickupAddress,
        dropoffAddress,
      };
      sessionStorage.setItem(BOOKING_DRAFT_KEY(businessId), JSON.stringify(draft));
    } catch {
      // ignore
    }
  }
  function goToCustomerAuth(path: string) {
    saveBookingDraft();
    router.push(returnTo ? `${path}?returnTo=${encodeURIComponent(returnTo)}` : path);
  }
  const formConfig = getBookingFormConfig(businessType, featureFlags);
  const isPropertyRental = businessType === "PROPERTY_RENTAL";

  // Property rentals: the server prices the stay night by night (seasonal or base rate), the same
  // way POST /api/book charges it. Amounts on this page come from that quote, never worked out here.
  const { quote: stayQuote, loading: stayQuoteLoading, error: stayQuoteError } = useStayQuote({
    enabled: isPropertyRental,
    businessId,
    serviceId,
    checkIn: checkInDate,
    checkOut: checkOutDate,
  });
  /** Price of what is being booked: the stay total for a property with dates, otherwise the service price. */
  const bookingPriceCents = isPropertyRental && stayQuote ? stayQuote.totalCents : selectedService?.priceCents ?? 0;
  const selectedDepositCents = selectedService
    ? displayDepositCents(selectedService.depositCents, bookingPriceCents, acceptsPayments)
    : null;

  // Determine if this is a stepped service flow
  // Always-stepped types (barber, salon, spa) use the flow regardless of the flag
  // Other types only use it when requiresStaffSelection is explicitly enabled
  const isServiceFlow =
    ALWAYS_STEPPED_TYPES.includes(businessType) ||
    (featureFlags.requiresStaffSelection && OPTIONAL_STEPPED_TYPES.includes(businessType));
  const hasMultipleLocations = locations.length > 1;

  // Notify parent of initial service/property selection
  useEffect(() => {
    if (serviceId && onServiceIdChange) onServiceIdChange(serviceId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync service selection when parent selects a service (e.g. from Services & Pricing list)
  useEffect(() => {
    if (initialServiceId && services.some((s) => s.id === initialServiceId)) {
      setServiceId(initialServiceId);
    }
  }, [initialServiceId, services]);

  // Load signed-in customer profile to prefill details
  useEffect(() => {
    let cancelled = false;
    getCurrentCustomer().then((profile) => {
      if (!cancelled && profile) {
        setCustomerProfile(profile);
        setEmail(profile.email);
        if (profile.name) setName(profile.name);
        if (profile.phone) setPhone(profile.phone);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Fetch customer loyalty when signed in and business has loyalty program
  useEffect(() => {
    if (!loyaltyProgram || !businessId) return;
    let cancelled = false;
    (async () => {
      const token = await getCustomerToken();
      if (!token || cancelled) return;
      try {
        const res = await fetch(`${getApiUrl()}/api/customer/loyalty?businessId=${encodeURIComponent(businessId)}`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setCustomerLoyalty({ points: data.points ?? 0, stamps: data.stamps ?? 0, emailVerified: data.emailVerified !== false });
        }
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [businessId, loyaltyProgram]);

  // Restore booking draft when returning from sign in / sign up
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(BOOKING_DRAFT_KEY(businessId));
      if (!raw) return;
      const draft = JSON.parse(raw) as Partial<{
        serviceId: string; locationId: string; staffId: string; date: string; selectedTime: string;
        checkInDate: string; checkOutDate: string; currentStep: BookingStep; email: string; name: string; phone: string;
        notes: string; marketingOptIn: boolean; intakeData: Record<string, string>; partySize: number; dietaryRestrictions: string;
        pickupAddress: string; dropoffAddress: string;
      }>;
      sessionStorage.removeItem(BOOKING_DRAFT_KEY(businessId));
      if (draft.serviceId && services.some((s) => s.id === draft.serviceId)) setServiceId(draft.serviceId);
      if (draft.locationId != null) setLocationId(draft.locationId);
      if (draft.staffId != null) setStaffId(draft.staffId);
      if (draft.date != null) setDate(draft.date);
      if (draft.selectedTime != null) setSelectedTime(draft.selectedTime);
      if (draft.checkInDate != null) setCheckInDate(draft.checkInDate);
      if (draft.checkOutDate != null) setCheckOutDate(draft.checkOutDate);
      if (draft.currentStep != null) setCurrentStep(draft.currentStep);
      if (draft.email != null) setEmail(draft.email);
      if (draft.name != null) setName(draft.name);
      if (draft.phone != null) setPhone(draft.phone);
      if (draft.notes != null) setNotes(draft.notes);
      if (typeof draft.marketingOptIn === "boolean") setMarketingOptIn(draft.marketingOptIn);
      if (draft.intakeData && typeof draft.intakeData === "object") setIntakeData(draft.intakeData);
      if (typeof draft.partySize === "number") setPartySize(draft.partySize);
      if (draft.dietaryRestrictions != null) setDietaryRestrictions(draft.dietaryRestrictions);
      if (draft.pickupAddress != null) setPickupAddress(draft.pickupAddress);
      if (draft.dropoffAddress != null) setDropoffAddress(draft.dropoffAddress);
    } catch {
      // ignore invalid draft
    }
  }, [businessId, services]);

  // Notify parent when email changes (so driving instructor page can show "your packages" and allow selecting)
  useEffect(() => {
    onCustomerEmailChange?.(email);
  }, [email, onCustomerEmailChange]);

  // Pre-fill email when reaching Confirm step if parent set it (e.g. from package card)
  useEffect(() => {
    if (currentStep === "details" && !email.trim() && initialEmail?.trim()) {
      setEmail(initialEmail.trim());
    }
  }, [currentStep, initialEmail]); // eslint-disable-line react-hooks/exhaustive-deps -- only prefill when landing on details with empty email

  // Fetch customer's lesson package purchases (driving instructor) when email is entered – only when not controlled by parent
  useEffect(() => {
    if (publicPackagePurchasesProp !== undefined) return;
    if (businessType !== "DRIVING_INSTRUCTOR" || !businessId || isDemo) {
      setPublicPackagePurchasesInternal([]);
      setSelectedPublicPackageIdInternal("");
      return;
    }
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setPublicPackagePurchasesInternal([]);
      setSelectedPublicPackageIdInternal("");
      return;
    }
    let cancelled = false;
    const apiUrl = getApiUrl();
    const params = new URLSearchParams({ businessId, email: trimmed });
    fetch(`${apiUrl}/api/public/lesson-packages/purchases?${params.toString()}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list: Array<{ id: string; lessonPackageName: string; lessonsRemaining: number }>) => {
        if (!cancelled) {
          setPublicPackagePurchasesInternal(Array.isArray(list) ? list : []);
          setSelectedPublicPackageIdInternal((prev) => {
            const stillValid = Array.isArray(list) && list.some((p) => p.id === prev);
            return stillValid ? prev : "";
          });
        }
      })
      .catch(() => {
        if (!cancelled) setPublicPackagePurchasesInternal([]);
      });
    return () => { cancelled = true; };
  }, [businessType, businessId, email, isDemo, publicPackagePurchasesProp]);

  // Fetch staff when needed (for service flow: after service + location selected)
  const fetchStaff = useCallback(async () => {
    // Staff needed for always-stepped types OR when requiresStaffSelection is on
    const needsStaff = ALWAYS_STEPPED_TYPES.includes(businessType) || featureFlags.requiresStaffSelection;
    if (!needsStaff) return;

    setLoadingStaff(true);
    try {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams({ businessId });
      if (locationId) params.set("locationId", locationId);
      if (serviceId) params.set("serviceId", serviceId);

      const res = await fetch(`${apiUrl}/api/public/staff?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setStaff(data);
      }
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    } finally {
      setLoadingStaff(false);
    }
  }, [businessId, businessType, locationId, serviceId, featureFlags.requiresStaffSelection]);

  // For service flow: fetch staff when we enter the professional step
  useEffect(() => {
    if (isServiceFlow && currentStep === "professional") {
      fetchStaff();
    }
  }, [isServiceFlow, currentStep, fetchStaff]);

  // For non-service-flow with staff selection: fetch staff on mount
  useEffect(() => {
    if (!isServiceFlow && featureFlags.requiresStaffSelection) {
      fetchStaff();
    }
  }, [isServiceFlow, featureFlags.requiresStaffSelection, fetchStaff]);

  // Fetch unavailable dates (blocked + booked) when a property is selected
  useEffect(() => {
    if (!isPropertyRental || !businessId || isDemo) return;
    if (!serviceId) {
      setUnavailableRanges([]);
      return;
    }
    let cancelled = false;
    const from = new Date();
    const to = new Date();
    to.setFullYear(to.getFullYear() + 1);
    const fromStr = from.toISOString().slice(0, 10);
    const toStr = to.toISOString().slice(0, 10);
    fetch(`${getApiUrl()}/api/public/unavailable-dates?businessId=${businessId}&serviceId=${encodeURIComponent(serviceId)}&from=${fromStr}&to=${toStr}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.unavailableRanges && Array.isArray(data.unavailableRanges)) {
          const ranges = data.unavailableRanges.map((r: { startDate: string; endDate: string }) => ({
            startDate: r.startDate?.slice(0, 10) ?? r.startDate,
            endDate: r.endDate?.slice(0, 10) ?? r.endDate,
          }));
          setUnavailableRanges(ranges);
        }
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [isPropertyRental, businessId, serviceId, isDemo]);

  const isDateUnavailable = (dateStr: string): boolean =>
    unavailableRanges.some((r) => dateStr >= r.startDate && dateStr <= r.endDate);

  const propertyRentalRangeOverlapsBlocked = Boolean(
    isPropertyRental &&
    checkInDate &&
    checkOutDate &&
    (() => {
      const start = new Date(checkInDate);
      const end = new Date(checkOutDate);
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const dayStr = d.toISOString().slice(0, 10);
        if (isDateUnavailable(dayStr)) return true;
      }
      return false;
    })()
  );

  // Fetch available slots when date or service changes (for non-property-rental)
  useEffect(() => {
    if (isPropertyRental) return;
    if (date && serviceId) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
      setDisplaySlots([]);
      // Don't clear selectedTime here: on return-from-login the restore effect may have just set it; this effect can run in the same commit with stale date/serviceId and would otherwise wipe the restored slot.
    }
  }, [date, serviceId, staffId, isPropertyRental]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Pre-flight checks before sending the request. */
  function runPreFlightChecks(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!serviceId) errors.push("Please select a " + (isPropertyRental ? "property" : "service") + ".");
    if (!email?.trim() && !phone?.trim()) errors.push("Email or phone number is required.");
    if (!name?.trim()) errors.push("Name is required.");
    if (isPropertyRental) {
      if (!checkInDate) errors.push("Check-in date is required.");
      if (!checkOutDate) errors.push("Check-out date is required.");
      if (checkInDate && checkOutDate) {
        const checkIn = new Date(checkInDate);
        const checkOut = new Date(checkOutDate);
        if (checkOut <= checkIn) errors.push("Check-out date must be after check-in date.");
        else {
          const svc = services.find((s) => s.id === serviceId);
          if (stayQuote) {
            // The server's minimum stay for these dates (a season can raise the property minimum)
            if (stayQuote.minStayError) errors.push(stayQuote.minStayError);
          } else if (svc && svc.durationMinutes >= 1440) {
            const minNights = Math.round(svc.durationMinutes / 1440);
            const startDay = new Date(checkInDate);
            startDay.setHours(0, 0, 0, 0);
            const endDay = new Date(checkOutDate);
            endDay.setHours(0, 0, 0, 0);
            const nights = Math.round((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000));
            if (nights < minNights) {
              errors.push(
                `Minimum stay is ${minNights} night${minNights !== 1 ? "s" : ""}. Your selection is ${nights} night${nights !== 1 ? "s" : ""}.`
              );
            }
          }
          const start = new Date(checkInDate);
          const end = new Date(checkOutDate);
          for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
            const dayStr = d.toISOString().slice(0, 10);
            if (unavailableRanges.some((r) => dayStr >= r.startDate && dayStr <= r.endDate)) {
              errors.push("Some of the selected dates are unavailable. Please choose different dates.");
              break;
            }
          }
        }
      }
    } else {
      if (!date) errors.push("Date is required.");
      if (!selectedTime) errors.push("Please select an available time.");
    }

    if (isServiceFlow && staff.length > 0 && !staffId) {
      errors.push(businessType === "DRIVING_INSTRUCTOR" ? "Please select an instructor." : "Please select a professional.");
    } else if (!isServiceFlow && featureFlags.requiresStaffSelection && !staffId) errors.push("Please select a staff member.");

    if (featureFlags.requiresIntakeForm && (!intakeData.symptoms || !intakeData.symptoms.trim())) {
      errors.push("Please complete the medical information form.");
    }

    if (isServiceFlow && hasMultipleLocations && !locationId) {
      errors.push("Please select a location.");
    }

    return { valid: errors.length === 0, errors };
  }

  async function fetchAvailableSlots() {
    if (!date || !serviceId) return;

    const previousSelectedTime = selectedTime;

    // Demo mode: generate mock hourly slots
    if (isDemo) {
      setLoadingSlots(true);
      setTimeout(() => {
        const mockSlots = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"];
        setAvailableSlots(mockSlots);
        setDisplaySlots(
          mockSlots.map((time) => ({ time, remaining: 1, firstAvailableAt: time }))
        );
        if (previousSelectedTime && !mockSlots.includes(previousSelectedTime)) setSelectedTime("");
        setLoadingSlots(false);
      }, 300);
      return;
    }

    setLoadingSlots(true);
    try {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams({
        businessId,
        date,
        serviceId,
      });

      // Pass staffId for per-staff availability
      if (staffId) {
        params.set("staffId", staffId);
      }
      // Pass locationId to filter slots by location (staff who work at this location)
      if (locationId) {
        params.set("locationId", locationId);
      }
      // Use business-configured display window (API returns displaySlots with remaining count)
      params.set("displaySlotMinutes", String(bookingSlotDisplayMinutes ?? 60));

      const res = await fetch(`${apiUrl}/api/public/available-slots?${params.toString()}`);
      const data = await res.json();
      if (res.ok) {
        const slots = data.slots || [];
        const displaySlots = data.displaySlots || [];
        setAvailableSlots(slots);
        setDisplaySlots(displaySlots);
        const slotTimes = new Set<string>([
          ...slots,
          ...displaySlots.map((ds: { time?: string; firstAvailableAt?: string }) => ds.time ?? ds.firstAvailableAt).filter(Boolean),
        ]);
        if (previousSelectedTime && !slotTimes.has(previousSelectedTime)) setSelectedTime("");
        if ((displaySlots.length ?? slots.length ?? 0) === 0) {
          setMessage("No available slots for this date. Please select another date.");
          setStatus("error");
        } else {
          if (!errorFromSubmitRef.current) {
            setMessage("");
            setStatus("idle");
          }
        }
      } else {
        setAvailableSlots([]);
        setDisplaySlots([]);
        setSelectedTime("");
        setMessage(data.error || "Failed to load available slots");
        setStatus("error");
      }
    } catch (error) {
      setAvailableSlots([]);
      setDisplaySlots([]);
      setMessage("Network error. Please try again.");
      setStatus("error");
    } finally {
      setLoadingSlots(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    errorFromSubmitRef.current = false;
    setMessage("");
    setSuggestedRanges([]);

    const preFlight = runPreFlightChecks();
    if (!preFlight.valid) {
      setMessage(preFlight.errors.length === 1 ? preFlight.errors[0] : preFlight.errors.map((e) => `• ${e}`).join("\n"));
      setStatus("error");
      return;
    }

    setStatus("loading");

    // Demo mode: simulate booking submission
    if (isDemo) {
      setTimeout(() => {
        setStatus("success");
        setMessage(
          isPropertyRental
            ? "Booking request submitted! This is a demonstration - no actual booking was created."
            : "Demo booking submitted! This is a demonstration - no actual booking was created. Sign up to create your own booking page!"
        );
        if (onDemoSuccess) {
          setTimeout(() => onDemoSuccess(), 1500);
        }
      }, 1000);
      return;
    }

    const apiUrl = getApiUrl();

    const startAt = isPropertyRental
      ? `${checkInDate}T14:00:00`
      : `${date}T${selectedTime}`;
    const endAt = isPropertyRental
      ? `${checkOutDate}T11:00:00`
      : undefined;

    const customerToken = await getCustomerToken();
    const bookingData: Record<string, unknown> = {
      businessId,
      serviceId,
      startAt,
      customer: {
        email: email.trim() || undefined,
        name: name || undefined,
        phone: phone.trim() || undefined,
        marketingOptIn,
      },
    };
    if (endAt) bookingData.endAt = endAt;
    if (staffId) bookingData.staffId = staffId;
    if (locationId) bookingData.locationId = locationId;
    if (pickupAddress?.trim()) bookingData.pickupAddress = pickupAddress.trim();
    if (dropoffAddress?.trim()) bookingData.dropoffAddress = dropoffAddress.trim();
    if (businessType === "DRIVING_INSTRUCTOR" && selectedPublicPackageId) {
      bookingData.lessonPackagePurchaseId = selectedPublicPackageId;
    }
    if (submitPayWithCashRef.current) {
      bookingData.payWithCash = true;
    }
    if (loyaltyRedemption) {
      bookingData.loyaltyRedemption = loyaltyRedemption;
    }

    const customNotes: string[] = [];
    if (notes) customNotes.push(notes);
    if (partySize > 1) customNotes.push(`Party size: ${partySize}`);
    if (dietaryRestrictions) customNotes.push(`Dietary: ${dietaryRestrictions}`);
    if (Object.keys(intakeData).length > 0) {
      customNotes.push(`Medical Info: ${JSON.stringify(intakeData)}`);
    }
    if (customNotes.length > 0) {
      bookingData.notes = customNotes.join("\n");
    }
    if (cartItems.length > 0) {
      bookingData.cartLineItems = cartItems.map((i) => ({
        productId: i.productId,
        ...(i.productVariantId ? { productVariantId: i.productVariantId } : {}),
        quantity: i.quantity,
      }));
    }

    let res: Response;
    let data: { error?: string; requiresApproval?: boolean; status?: string; clientSecret?: string; amountCents?: number; priceCents?: number; bookingId?: string; paymentIntentId?: string; payToken?: string | null; payUrl?: string | null };
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (customerToken) headers.Authorization = `Bearer ${customerToken}`;
      res = await fetch(`${apiUrl}/api/book`, {
        method: "POST",
        headers,
        body: JSON.stringify(bookingData),
      });
      const text = await res.text();
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { error: res.ok ? "Invalid response" : "Booking failed" };
      }
    } catch {
      errorFromSubmitRef.current = true;
      setMessage("Network error. Please check your connection and try again.");
      setStatus("error");
      return;
    }

    if (!res.ok) {
      errorFromSubmitRef.current = true;
      setMessage(typeof data?.error === "string" ? data.error : "Booking failed. Please try again.");
      setStatus("error");
      if (res.status === 409 && isPropertyRental && checkInDate && checkOutDate) {
        const start = new Date(checkInDate);
        const end = new Date(checkOutDate);
        const nights = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
        const fromStr = new Date().toISOString().slice(0, 10);
        const toDate = new Date();
        toDate.setFullYear(toDate.getFullYear() + 1);
        const toStr = toDate.toISOString().slice(0, 10);
        fetch(
          `${getApiUrl()}/api/public/suggested-ranges?businessId=${businessId}&serviceId=${encodeURIComponent(serviceId)}&nights=${nights}&from=${fromStr}&to=${toStr}&limit=5`
        )
          .then((r) => r.json())
          .then((d) => {
            if (d.suggestedRanges && Array.isArray(d.suggestedRanges)) {
              setSuggestedRanges(
                d.suggestedRanges.map((r: { startDate: string; endDate: string }) => ({
                  startDate: r.startDate?.slice(0, 10) ?? r.startDate,
                  endDate: r.endDate?.slice(0, 10) ?? r.endDate,
                }))
              );
            }
          })
          .catch(() => { });
      }
      if (!isPropertyRental && date && serviceId) {
        fetchAvailableSlots();
      }
      return;
    }

    // If payment is needed, show payment form (snapshot add-ons that were actually included in this booking)
    if (data.clientSecret && data.amountCents) {
      setConfirmedAddons([...cartItems]);
      setLastBookingId(data.bookingId ?? null);
      setLastPayToken(data.payToken ?? null);
      setPaymentIntentId(data.paymentIntentId ?? null);
      setPaymentClientSecret(data.clientSecret);
      setPaymentAmount(data.amountCents);
      setShowPayment(true);
      onPaymentStepChange?.(true);
      setStatus("idle");
      return;
    }

    console.log("[BookingForm] booking success (no payment step): clearing cart");
    setConfirmedAddons([...cartItems]);
    setStatus("success");
    clearCart();
    const wasPayWithCash = submitPayWithCashRef.current;
    submitPayWithCashRef.current = false;

    // Build booking details for success message
    const bookingDetails: string[] = [];
    if (selectedService) {
      if (isPropertyRental) {
        bookingDetails.push(`${selectedService.name}`);
      } else {
        bookingDetails.push(`${selectedService.name} (${selectedService.durationMinutes} min)`);
      }
    }
    if (date && selectedTime) {
      const bookingDate = new Date(`${date}T12:00:00Z`);
      const dateStr = bookingDate.toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long", year: "numeric" });
      bookingDetails.push(`${dateStr} at ${selectedTime}`);
    } else if (isPropertyRental && checkInDate && checkOutDate) {
      const checkIn = new Date(`${checkInDate}T12:00:00Z`);
      const checkOut = new Date(`${checkOutDate}T12:00:00Z`);
      const checkInStr = checkIn.toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long", year: "numeric" });
      const checkOutStr = checkOut.toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long", year: "numeric" });
      bookingDetails.push(`${checkInStr} - ${checkOutStr}`);
    }
    
    // Add deposit information for property rental requests
    const stayTotalCents = typeof data.priceCents === "number" ? data.priceCents : bookingPriceCents;
    const requestDeposit = selectedService
      ? displayDepositCents(selectedService.depositCents, stayTotalCents, acceptsPayments)
      : null;
    if (isPropertyRental && selectedService && (data.requiresApproval || data.status === "pending") && requestDeposit != null) {
      const remainingAmount = stayTotalCents - requestDeposit;
      bookingDetails.push(`Deposit: ${formatPounds(requestDeposit)}${remainingAmount > 0 ? ` (remaining ${formatPounds(remainingAmount)} due on arrival)` : ""}`);
    }
    
    const detailsText = bookingDetails.length > 0 ? `\n\n${bookingDetails.join("\n")}` : "";
    
    setMessage(
      wasPayWithCash
        ? `Booking confirmed! Pay by cash when you arrive, nothing to pay now. You'll receive a confirmation email shortly.${detailsText}`
        : data.requiresApproval || data.status === "pending"
        ? `Booking submitted! The business will confirm your request shortly. You'll receive an email once it's approved.${detailsText}`
        : `Booking confirmed! You will receive a confirmation email shortly.${detailsText}`
    );
    // Stepped service flow: stay on details step so user sees the success message
    if (!isServiceFlow) {
      resetForm();
    }
  }

  function resetForm() {
    setServiceId(services[0]?.id ?? "");
    setLocationId("");
    setStaffId("");
    setDate("");
    setSelectedTime("");
    setCheckInDate("");
    setCheckOutDate("");
    setSuggestedRanges([]);
    setEmail("");
    setName("");
    setPhone("");
    setNotes("");
    setIntakeData({});
    setPartySize(1);
    setDietaryRestrictions("");
    setPickupAddress("");
    setDropoffAddress("");
    setAvailableSlots([]);
    setCurrentStep("service");
    setPayLaterLink(null);
    setConfirmedAddons([]);
  }

  const minDate = new Date().toISOString().slice(0, 10);
  const minCheckOutDate = checkInDate
    ? (() => {
      const d = new Date(checkInDate);
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    })()
    : minDate;

  const propertyRentalNightsTooShort = Boolean(
    isPropertyRental &&
    checkInDate &&
    checkOutDate &&
    new Date(checkOutDate) > new Date(checkInDate) &&
    (() => {
      if (stayQuote) return Boolean(stayQuote.minStayError);
      const svc = services.find((s) => s.id === serviceId);
      if (!svc || svc.durationMinutes < 1440) return false;
      const minNights = Math.round(svc.durationMinutes / 1440);
      const startDay = new Date(checkInDate);
      startDay.setHours(0, 0, 0, 0);
      const endDay = new Date(checkOutDate);
      endDay.setHours(0, 0, 0, 0);
      const nights = Math.round((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000));
      return nights < minNights;
    })()
  );

  if (bookingDisabled) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 shadow-lg">
        <p className="font-medium text-foreground">Booking is temporarily unavailable</p>
        <p className="mt-1 text-sm text-muted-foreground">
          This business is not currently accepting online bookings. You can still browse services and contact them directly.
        </p>
        <Link
          href="/help/booking-unavailable"
          className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
        >
          Why am I seeing this?
        </Link>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-6 text-center text-muted-foreground">
        {isPropertyRental ? "This business has no bookable properties yet." : "This business has no bookable services yet."}
      </div>
    );
  }

  // Payment step: show Stripe Elements form with breakdown (only what was included in this booking – use confirmedAddons, not current cart)
  if (showPayment && paymentClientSecret) {
    const serviceCents = bookingPriceCents;
    const hasAddons = confirmedAddons.length > 0;

    const handleBackFromPayment = async () => {
      if (lastBookingId && businessSlug) {
        try {
          const res = await fetch(`${getApiUrl()}/api/book/release-pending`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bookingId: lastBookingId, businessSlug, customerEmail: email }),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setMessage(data?.error ?? "Could not release the slot. Please try another time.");
            setStatus("error");
          }
        } catch {
          setMessage("Could not release the slot. Please try another time.");
          setStatus("error");
        }
      }
      setLastBookingId(null);
      setShowPayment(false);
      onPaymentStepChange?.(false);
      setPaymentClientSecret(null);
      setPaymentAmount(0);
      setPaymentIntentId(null);
    };

    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={handleBackFromPayment}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back
        </button>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <h3 className="font-semibold text-foreground mb-3">Order summary</h3>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="font-medium text-muted-foreground">Service</dt>
              <dd className="mt-0.5 flex justify-between gap-4">
                <span className="text-foreground">{selectedService?.name ?? "-"}</span>
                <span className="tabular-nums text-foreground">£{(serviceCents / 100).toFixed(2)}</span>
              </dd>
            </div>
            {hasAddons && (
              <div>
                <dt className="font-medium text-muted-foreground mt-2">Add-ons</dt>
                <dd className="mt-0.5 space-y-1">
                  <span className="block font-medium text-muted-foreground text-xs uppercase tracking-wide">Products</span>
                  {confirmedAddons.map((item, idx) => (
                    <div key={`${item.productId}-${item.productVariantId ?? ""}-${idx}`} className="flex justify-between gap-4 pl-2 text-foreground">
                      <span>
                        {item.productName}
                        {item.variantName ? ` (${item.variantName})` : ""}
                        {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                      </span>
                      <span className="tabular-nums">£{((item.unitPriceCents * item.quantity) / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </dd>
              </div>
            )}
            <div className="border-t border-border pt-2 mt-2">
              <dt className="sr-only">Total</dt>
              <dd className="flex justify-between gap-4 font-semibold text-foreground">
                <span>Total</span>
                <span className="tabular-nums">£{(paymentAmount / 100).toFixed(2)}</span>
              </dd>
            </div>
          </dl>
        </div>
        <PaymentForm
          clientSecret={paymentClientSecret}
          amountCents={paymentAmount}
          showSkip={false}
          onSuccess={async () => {
            console.log("[BookingForm] payment onSuccess: clearing cart and continuing");
            setShowPayment(false);
            onPaymentStepChange?.(false);
            clearCart();
            const currentPaymentIntentId = paymentIntentId;
            setPaymentClientSecret(null);
            setStatus("loading");
            setMessage("Payment successful! Creating your booking...");

            // Extract paymentIntentId from clientSecret if not stored
            let intentId = currentPaymentIntentId;
            if (!intentId && paymentClientSecret) {
              // clientSecret format: pi_xxx_secret_yyy
              const match = paymentClientSecret.match(/^(pi_[^_]+)/);
              if (match) {
                intentId = match[1];
              }
            }

            // Sync payment state (order completion / stock decrement, booking updates). Idempotent if webhook already ran.
            if (intentId) {
              try {
                const apiUrl = getApiUrl();
                const syncRes = await fetch(`${apiUrl}/api/book/sync-payment`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ paymentIntentId: intentId }),
                });
                if (syncRes.ok) {
                  const syncData = await syncRes.json();
                  if (syncData.synced) console.log("[BookingForm] Payment synced (order/booking updated)");
                }
              } catch (e) {
                console.warn("[BookingForm] Sync payment failed:", e);
              }
            }

            // Wait a moment for sync/webhook to process
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Build booking details message
            const details: string[] = [];
            if (selectedService) details.push(selectedService.name);
            if (date && selectedTime) {
              const bookingDate = new Date(`${date}T12:00:00Z`);
              const dateStr = bookingDate.toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long", year: "numeric" });
              details.push(`${dateStr} at ${selectedTime}`);
            }
            if (staffId && staff.length > 0) {
              const selectedStaff = staff.find((s) => s.id === staffId);
              if (selectedStaff) details.push(`with ${selectedStaff.name}`);
            }
            const detailsText = details.length > 0 ? `\n\n${details.join("\n")}` : "";
            
            setStatus("success");
            setMessage(`Payment successful! Your booking is confirmed.${detailsText}`);
          }}
          onCancel={() => {
            setShowPayment(false);
            onPaymentStepChange?.(false);
            setPaymentClientSecret(null);
            setStatus("success");
            // Card checkout holds have no pay link token: the slot is only held while paying now
            const link = businessSlug && lastBookingId && lastPayToken
              ? `/book/${businessSlug}/pay?bookingId=${lastBookingId}&token=${encodeURIComponent(lastPayToken)}`
              : null;
            setPayLaterLink(link ?? null);
            setMessage(
              link
                ? "Booking created! You can pay now or at the time of your appointment."
                : "Booking created! You can pay later or at the time of your appointment."
            );
            setLastBookingId(null);
          }}
        />
      </div>
    );
  }

  // ==========================================
  // STEPPED SERVICE FLOW (barber, salon, etc.)
  // ==========================================
  if (isServiceFlow) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 shadow-lg space-y-5">
        {/* Progress indicator */}
        <StepIndicator
          steps={getStepsForBusiness(hasMultipleLocations)}
          currentStep={currentStep}
        />

        {/* Step: Select Service */}
        {currentStep === "service" && (
          <div className="space-y-4">
            <div className="grid gap-3">
              {(serviceId ? services.filter((s) => s.id === serviceId) : services).map((s) => {
                const thumb = s.images?.[0]?.url;
                const isSelected = serviceId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setServiceId(s.id);
                      onServiceIdChange?.(s.id);
                    }}
                    className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                      isSelected
                        ? "border-primary bg-muted/50 ring-1 ring-primary/15"
                        : "border-input bg-background hover:bg-muted/50"
                    }`}
                  >
                    {/* Thumbnail */}
                    {thumb && (
                      <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={thumb} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-foreground">{s.name}</p>
                        <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                          £{(s.priceCents / 100).toFixed(2)}
                        </p>
                      </div>
                      {s.description && (
                        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{s.description}</p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          {s.durationMinutes} min
                        </span>
                        {(() => {
                          const dep = displayDepositCents(s.depositCents, s.priceCents, acceptsPayments);
                          return dep != null ? (
                            <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                              {formatPounds(dep)} deposit
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    {/* Checkmark */}
                    {isSelected && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              disabled={!serviceId}
              onClick={() => {
                if (hasMultipleLocations) {
                  setCurrentStep("location");
                } else {
                  // Auto-select single location if exists
                  if (locations.length === 1) setLocationId(locations[0].id);
                  setCurrentStep("professional");
                }
              }}
              className="w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step: Select Location */}
        {currentStep === "location" && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Choose a Location</h3>
            <LocationSelector
              locations={locations}
              value={locationId}
              onChange={(id) => {
                setLocationId(id);
                setStaffId(""); // reset staff when location changes
              }}
              required
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCurrentStep("service")}
                className="flex-1 rounded-lg border border-input bg-background py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                disabled={!locationId}
                onClick={() => setCurrentStep("professional")}
                className="flex-1 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step: Select Professional / Instructor */}
        {currentStep === "professional" && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              {businessType === "DRIVING_INSTRUCTOR" ? "Choose your instructor" : "Choose a Professional"}
            </h3>
            {loadingStaff ? (
              <div className="text-sm text-muted-foreground py-4">
                {businessType === "DRIVING_INSTRUCTOR" ? "Loading instructors…" : "Loading professionals..."}
              </div>
            ) : (
              <StaffSelector
                staff={staff}
                value={staffId}
                onChange={setStaffId}
                required={staff.length > 0}
                showAnyOption
                label={businessType === "DRIVING_INSTRUCTOR" ? "Who would you like to learn with?" : "Who would you like?"}
              />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  if (hasMultipleLocations) {
                    setCurrentStep("location");
                  } else {
                    setCurrentStep("service");
                  }
                }}
                className="flex-1 rounded-lg border border-input bg-background py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                disabled={staff.length > 0 && !staffId}
                onClick={() => setCurrentStep("datetime")}
                className="flex-1 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step: Select Date & Time */}
        {currentStep === "datetime" && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Pick a Date & Time</h3>

            {/* Summary of selections so far */}
            <div className="rounded-lg bg-muted/50 p-3 space-y-2 border border-border/50">
              {selectedService && (
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{selectedService.name}</span> &middot; {selectedService.durationMinutes} min &middot; £{(selectedService.priceCents / 100).toFixed(2)}
                  </p>
                </div>
              )}
              {staffId && (
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {staffId === "any" ? "Any Available Professional" : (() => { const s = staff.find((s) => s.id === staffId); return s ? `${s.name}${s.title ? ` · ${s.title}` : ""}` : ""; })()}
                    </span>
                  </p>
                </div>
              )}
              {locationId && locations.length > 0 && (() => {
                const loc = locations.find((l) => l.id === locationId);
                return loc ? (
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{loc.name}</span>
                      {loc.address && <span> · {loc.address}</span>}
                    </p>
                  </div>
                ) : null;
              })()}
            </div>

            <div className="space-y-4">
              <div className="h-[330px]">
                <label className="block text-sm font-medium text-foreground mb-2">Date *</label>
                <BookingDateCalendar
                  value={date}
                  onChange={(d) => {
                    setDate(d);
                    setSelectedTime("");
                  }}
                  minDate={minDate}
                />
              </div>

              {date && serviceId && (
                <div className="mt-2">
                  <label className="block text-sm font-medium mb-2">Available Times *</label>
                {loadingSlots ? (
                  <div className="text-sm text-muted-foreground py-2">Loading available slots...</div>
                ) : (() => {
                  const filteredDisplaySlots = filterPastDisplaySlots(displaySlots);
                  const filteredAvailableSlots = filterPastTimes(availableSlots);
                  return filteredDisplaySlots.length === 0 && filteredAvailableSlots.length === 0 ? (
                    <div className="text-sm text-destructive py-2">
                      No available slots for this date. Please select another date.
                    </div>
                  ) : filteredDisplaySlots.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {filteredDisplaySlots.map((ds) => (
                        <button
                          key={ds.time}
                          type="button"
                          onClick={() => {
                            setSelectedTime(ds.firstAvailableAt);
                            setMessage("");
                            setStatus("idle");
                          }}
                          className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                            selectedTime === ds.firstAvailableAt
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background hover:bg-muted border-input"
                          }`}
                        >
                          {ds.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {filteredAvailableSlots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => {
                            setSelectedTime(time);
                            setMessage("");
                            setStatus("idle");
                          }}
                          className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                            selectedTime === time
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background hover:bg-muted border-input"
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  );
                })()}
                {selectedTime && selectedService && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Selected: {selectedTime} (Duration: {selectedService.durationMinutes} min)
                  </p>
                )}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCurrentStep("professional")}
                className="flex-1 rounded-lg border border-input bg-background py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                disabled={!selectedTime}
                onClick={() => setCurrentStep("details")}
                className="flex-1 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step: Customer Details & Confirm */}
        {currentStep === "details" && (
          <>
            {status === "loading" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-sm font-medium text-foreground">{message || "Creating your booking…"}</p>
              </div>
            ) : status === "success" ? (
              <div className="space-y-4 text-center py-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-foreground">Booking confirmed</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{message}</p>
                {/* Deposit paid info when customer paid deposit */}
                {selectedService && displayDepositCents(selectedService.depositCents, selectedService.priceCents, acceptsPayments) != null && paymentAmount > 0 && paymentAmount < selectedService.priceCents && (
                  <p className="text-sm text-muted-foreground rounded-md bg-muted/60 px-3 py-2 inline-block">
                    <span className="font-medium text-foreground">Deposit paid: £{(paymentAmount / 100).toFixed(2)}</span>
                    {" · "}
                    <span>£{((selectedService.priceCents - paymentAmount) / 100).toFixed(2)} due on arrival</span>
                  </p>
                )}
                {/* Booking details summary */}
                {(selectedService || (date && selectedTime)) && (
                  <div className="mt-4 rounded-lg bg-muted/50 p-4 text-left border border-border/50">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Booking Details</p>
                    <div className="space-y-2 text-sm">
                      {selectedService && (
                        <div className="flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                          <span className="text-foreground"><span className="font-medium">{selectedService.name}</span> · {selectedService.durationMinutes} min · £{(selectedService.priceCents / 100).toFixed(2)}</span>
                        </div>
                      )}
                      {date && selectedTime && (
                        <div className="flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          <span className="text-foreground">
                            {new Date(`${date}T12:00:00Z`).toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long", year: "numeric" })} at {selectedTime}
                          </span>
                        </div>
                      )}
                      {staffId && staff.length > 0 && (() => {
                        const selectedStaff = staff.find((s) => s.id === staffId);
                        return selectedStaff ? (
                          <div className="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                            </svg>
                            <span className="text-foreground">with <span className="font-medium">{selectedStaff.name}</span>{selectedStaff.title ? ` · ${selectedStaff.title}` : ""}</span>
                          </div>
                        ) : null;
                      })()}
                      {locationId && locations.length > 0 && (() => {
                        const loc = locations.find((l) => l.id === locationId);
                        return loc ? (
                          <div className="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                            </svg>
                            <span className="text-foreground">{loc.name}{loc.address ? ` · ${loc.address}` : ""}</span>
                          </div>
                        ) : null;
                      })()}
                      {confirmedAddons.length > 0 && (
                        <div className="border-t border-border/50 pt-2 mt-2 space-y-1.5">
                          <p className="text-xs font-medium text-muted-foreground">Add-ons purchased</p>
                          {confirmedAddons.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                              <span className="text-foreground">
                                {item.productName}
                                {item.variantName ? ` (${item.variantName})` : ""}
                                {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                              </span>
                              <span className="text-foreground font-medium whitespace-nowrap">
                                £{((item.unitPriceCents * item.quantity) / 100).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 justify-center flex-wrap">
                  {payLaterLink && (
                    <Link
                      href={payLaterLink}
                      className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-95 transition-opacity"
                    >
                      Pay now
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => { resetForm(); setPayLaterLink(null); }}
                    className="rounded-lg border border-input bg-background px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    Book another
                  </button>
                  <Link
                    href="/customer/appointments"
                    className="rounded-lg border border-primary bg-transparent px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-center"
                  >
                    My appointments
                  </Link>
                </div>
              </div>
            ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Your Details</h3>

            {/* Full booking summary */}
            <div className="rounded-lg bg-muted/50 p-4 space-y-3 border border-border">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Booking Summary</p>
              {selectedService && (
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">{selectedService.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedService.durationMinutes} min</p>
                  </div>
                  <p className="text-sm font-semibold text-foreground">£{(selectedService.priceCents / 100).toFixed(2)}</p>
                </div>
              )}
              {cartItems.length > 0 && (
                <div className="border-t border-border/50 pt-2 space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">Add-ons</p>
                  {cartItems.map((item, idx) => (
                    <div key={idx} className="flex items-start justify-between gap-2 text-sm">
                      <span className="text-foreground">
                        {item.productName}
                        {item.variantName ? ` (${item.variantName})` : ""}
                        {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                      </span>
                      <span className="text-foreground font-medium whitespace-nowrap">
                        £{((item.unitPriceCents * item.quantity) / 100).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t border-border/50 pt-2 space-y-1.5">
                {staffId && (() => {
                  const selectedStaff = staffId === "any" ? null : staff.find((s) => s.id === staffId);
                  return (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                      <span>
                        {staffId === "any"
                          ? "Any Available Professional"
                          : selectedStaff
                          ? `${selectedStaff.name}${selectedStaff.title ? ` · ${selectedStaff.title}` : ""}`
                          : ""}
                      </span>
                    </div>
                  );
                })()}
                {locationId && locations.length > 0 && (() => {
                  const loc = locations.find((l) => l.id === locationId);
                  return loc ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <span>{loc.name}{loc.address ? ` · ${loc.address}` : ""}</span>
                    </div>
                  ) : null;
                })()}
                {date && selectedTime && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    <span>{new Date(`${date}T12:00:00Z`).toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long" })} at {selectedTime}</span>
                  </div>
                )}
              </div>
              {/* Payment / deposit notice */}
              {acceptsPayments && selectedService && selectedService.priceCents > 0 && (
                <div className="border-t border-border/50 pt-2 space-y-1">
                  {selectedDepositCents != null ? (
                    <>
                      <p className="text-sm font-medium text-foreground">
                        Pay now: {formatPounds(selectedDepositCents)} deposit
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatPounds(selectedService.priceCents - selectedDepositCents)} due on arrival
                      </p>
                    </>
                  ) : (
                    <p className="text-sm font-medium text-foreground">
                      Pay now: £{(selectedService.priceCents / 100).toFixed(2)}
                    </p>
                  )}
                  {cartItems.length > 0 && (
                    <p className="text-sm text-foreground">
                      Add-ons: £{(cartItems.reduce((sum, i) => sum + i.unitPriceCents * i.quantity, 0) / 100).toFixed(2)}
                    </p>
                  )}
                </div>
              )}
              {/* Booking policy: deposit already above; cancel + 2h cutoff (client-fair, no surprise fees) */}
              <div className="border-t border-border/50 pt-2 space-y-1">
                <p className="text-xs font-medium text-foreground">Booking policy</p>
                <p className="text-xs text-muted-foreground">
                  {customerCancellationMode === "disabled"
                    ? "Online cancellation is not available. Contact the business to change or cancel."
                    : customerCancellationMode === "automatic"
                      ? (
                          <>
                            {cancellationFullRefundDays != null && (
                              <>Full refund if cancelled {cancellationFullRefundDays}+ days before. </>
                            )}
                            {cancellationPartialRefundPercent != null &&
                              cancellationPartialRefundDays != null &&
                              cancellationFullRefundDays != null &&
                              cancellationFullRefundDays > cancellationPartialRefundDays && (
                              <>{cancellationPartialRefundPercent}% refund if {cancellationPartialRefundDays} to {cancellationFullRefundDays - 1} days before. </>
                            )}
                            {(cancellationPartialRefundDays != null || cancellationFullRefundDays != null) && (
                              <>Less than {cancellationPartialRefundDays ?? cancellationFullRefundDays} days: no refund. </>
                            )}
                            {cancellationFullRefundDays == null && cancellationPartialRefundDays == null && (
                              <>You can cancel online; refunds follow this shop&apos;s policy. </>
                            )}
                          </>
                        )
                      : "You can request cancellation online; the business reviews and processes any refund."}
                </p>
                <p className="text-xs text-muted-foreground">
                  Online cancel or reschedule is not available within 2 hours of the appointment. Contact the business if you need to change it then.
                </p>
              </div>
            </div>

            {/* Loyalty redemption – when signed in and has balance */}
            {loyaltyProgram && acceptsPayments && selectedService && selectedService.priceCents > 0 && customerLoyalty && (
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Loyalty rewards</p>
                {!customerLoyalty.emailVerified && (
                  <p className="text-sm text-muted-foreground">
                    Verify your email to see all your bookings, including any made before you verified, and to use your rewards. You can resend the verification email from your account.
                  </p>
                )}
                {customerLoyalty.emailVerified && loyaltyProgram.pointsEnabled && customerLoyalty.points > 0 && loyaltyProgram.pointsToPence > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="loyalty"
                      checked={loyaltyRedemption?.type === "points"}
                      onChange={() => {
                        const pointsForFullPrice = pointsNeededForDiscount(selectedService.priceCents, loyaltyProgram.pointsToPence);
                        const pointsToRedeem = Math.min(customerLoyalty.points, pointsForFullPrice);
                        setLoyaltyRedemption({ type: "points", pointsToRedeem });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">Use {customerLoyalty.points} points (up to £{(pointsDiscountCents(customerLoyalty.points, loyaltyProgram.pointsToPence) / 100).toFixed(2)} off)</span>
                  </label>
                )}
                {customerLoyalty.emailVerified && loyaltyProgram.stampsEnabled && customerLoyalty.stamps >= loyaltyProgram.stampsRequired && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="loyalty"
                      checked={loyaltyRedemption?.type === "stamps"}
                      onChange={() => setLoyaltyRedemption({ type: "stamps" })}
                      className="rounded"
                    />
                    <span className="text-sm">Use stamp card ({customerLoyalty.stamps}/{loyaltyProgram.stampsRequired} stamps)</span>
                  </label>
                )}
                {(loyaltyRedemption?.type === "points" || loyaltyRedemption?.type === "stamps") && (
                  <button
                    type="button"
                    onClick={() => setLoyaltyRedemption(null)}
                    className="text-xs text-muted-foreground hover:underline"
                  >
                    Don&apos;t use rewards
                  </button>
                )}
              </div>
            )}

            {/* Type-specific fields */}
            {featureFlags.requiresIntakeForm && (
              <IntakeForm
                value={intakeData}
                onChange={(data) => setIntakeData(data)}
                required={true}
              />
            )}

            {featureFlags.allowsGroupBookings && businessType === "RESTAURANT" && (
              <PartySizeField
                value={partySize}
                onChange={setPartySize}
                required={true}
              />
            )}

            {businessType === "RESTAURANT" && (
              <DietaryRestrictionsField
                value={dietaryRestrictions}
                onChange={setDietaryRestrictions}
              />
            )}

            {/* Customer fields */}
            <p className="text-sm text-muted-foreground mb-2">We need at least one way to reach you.</p>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <p className="mt-1 text-xs text-muted-foreground">For your booking confirmation.</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Phone number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 07xxx xxxxxx"
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <p className="mt-1 text-xs text-muted-foreground">For reminders and day-of contact.</p>
            </div>

            {businessType === "DRIVING_INSTRUCTOR" && publicPackagePurchases.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-1">Pay with lesson package</label>
                <select
                  value={selectedPublicPackageId}
                  onChange={(e) => setSelectedPublicPackageId(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Pay normally</option>
                  {publicPackagePurchases.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.lessonPackageName} ({p.lessonsRemaining} left)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {customerProfile ? (
              <p className="text-sm text-muted-foreground">
                Signed in as {customerProfile.email}. Your details are saved for next time.{" "}
                Not you?{" "}
                <button type="button" onClick={() => { clearCustomerToken(); clearToken(); setCustomerProfile(null); router.refresh(); }} className="text-primary hover:underline">Log out</button>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                <button type="button" onClick={() => goToCustomerAuth("/auth/customer/login")} className="text-primary hover:underline">Sign in</button>
                {" or "}
                <button type="button" onClick={() => goToCustomerAuth("/auth/customer/signup")} className="text-primary hover:underline">create an account</button>
                {" to save your details for next time."}
              </p>
            )}

            {businessType === "DRIVING_INSTRUCTOR" && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Pickup address</label>
                  <input
                    type="text"
                    value={pickupAddress}
                    onChange={(e) => setPickupAddress(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                    placeholder="Where should we pick you up?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Drop-off address (optional)</label>
                  <input
                    type="text"
                    value={dropoffAddress}
                    onChange={(e) => setDropoffAddress(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                    placeholder="Where should we drop you off?"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Any additional notes or special requests"
              />
            </div>

            {message && (
              <div
                className={`rounded-md p-3 text-sm ${
                  status === "error"
                    ? "bg-destructive/10 text-destructive"
                    : "bg-muted text-muted-foreground"
                } ${status === "error" ? "whitespace-pre-line" : ""}`}
              >
                {message}
              </div>
            )}

            <div className="flex flex-col gap-2">
              {acceptsPayments && acceptCashPayments && selectedService && selectedService.priceCents > 0 ? (
                <>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentStep("datetime")}
                      className="flex-1 rounded-lg border border-input bg-background py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="flex-1 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
                      {...fsElement("Booking Pay Now Card Button", { id: "booking-pay-now-card", section: "booking-confirm" })}
                    >
                      Pay now (card)
                    </button>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        submitPayWithCashRef.current = true;
                        (e.target as HTMLButtonElement).closest("form")?.requestSubmit();
                      }}
                      className="w-full rounded-lg border border-primary bg-transparent py-3 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                      {...fsElement("Booking Pay When I Arrive Button", { id: "booking-pay-cash", section: "booking-confirm" })}
                    >
                      Pay when I arrive
                    </button>
                    <p className="text-xs text-muted-foreground text-center">
                      No payment now, pay by cash at your appointment
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentStep("datetime")}
                    className="flex-1 rounded-lg border border-input bg-background py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="flex-1 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
                    {...fsElement("Booking Confirm Button", { id: "booking-confirm", section: "booking-confirm" })}
                  >
                    {acceptCashPayments && selectedService && selectedService.priceCents > 0 && !acceptsPayments
                      ? "Confirm booking · Pay at appointment"
                      : "Confirm Booking"}
                  </button>
                </div>
              )}
            </div>
          </form>
            )}
          </>
        )}
      </div>
    );
  }

  // ==========================================
  // ORIGINAL FLOW (property rental + non-staff service types)
  // ==========================================
  const serviceLabel = isPropertyRental ? "Property" : "Service";
  const servicePlaceholder = isPropertyRental ? "Select property" : "Select service";

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border border-border bg-card p-6 shadow-lg space-y-5">
      {/* Price display for property rentals */}
      {isPropertyRental && selectedService && (
        <div className="pb-4 mb-4 border-b border-border">
          <PropertyStayPrice
            priceCents={selectedService.priceCents}
            fromNightlyCents={selectedService.fromNightlyCents}
            hasDates={Boolean(checkInDate && checkOutDate && checkOutDate > checkInDate)}
            quote={stayQuote}
            loading={stayQuoteLoading}
            error={stayQuoteError}
            depositCents={selectedDepositCents}
          />
          {/* Show address with Google Maps link (area only and no link when the shop hides its address) */}
          {selectedService.address && addressHidden && (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-sm text-muted-foreground mb-1">Area</p>
              <p className="text-sm text-foreground">{selectedService.address}</p>
            </div>
          )}
          {selectedService.address && !addressHidden && (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-sm text-muted-foreground mb-1">Address</p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedService.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                {selectedService.address}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Base Fields */}
      <div>
        <label className="block text-sm font-medium mb-1">{serviceLabel} *</label>
        <select
          value={serviceId}
          onChange={(e) => {
            const v = e.target.value;
            setServiceId(v);
            setSelectedTime("");
            onServiceIdChange?.(v);
          }}
          className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
          required
        >
          <option value="">{servicePlaceholder}</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {isPropertyRental
                ? `${s.name} · ${s.durationMinutes >= 1440 ? `${Math.round(s.durationMinutes / 1440)} night(s) min` : "no minimum"} · ${nightlyPriceLabel(s.priceCents, s.fromNightlyCents)}`
                : `${s.name} · ${s.durationMinutes} min · £${(s.priceCents / 100).toFixed(2)}`}
            </option>
          ))}
        </select>
      </div>

      {/* Property rental: DatePickerField with shouldDisableDate */}
      {isPropertyRental ? (
        <>
          <div>
            <DatePickerField
              label="Check-in date *"
              value={checkInDate}
              onChange={(d) => {
                setCheckInDate(d);
                setSuggestedRanges([]);
                if (checkOutDate && d && new Date(checkOutDate) <= new Date(d)) {
                  setCheckOutDate("");
                }
              }}
              minDate={minDate}
              required
              shouldDisableDate={isDateUnavailable}
            />
          </div>
          <div>
            <DatePickerField
              label="Check-out date *"
              value={checkOutDate}
              onChange={(d) => {
                setCheckOutDate(d);
                setSuggestedRanges([]);
              }}
              minDate={minCheckOutDate}
              required
              shouldDisableDate={isDateUnavailable}
            />
          </div>
          {checkInDate && checkOutDate && new Date(checkOutDate) <= new Date(checkInDate) && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <p className="text-sm text-destructive">Check-out must be after check-in.</p>
            </div>
          )}
          {propertyRentalRangeOverlapsBlocked && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <p className="text-sm text-destructive">Some of the selected dates are unavailable. Please choose different dates.</p>
            </div>
          )}
          {!stayQuote &&
            checkInDate &&
            checkOutDate &&
            new Date(checkOutDate) > new Date(checkInDate) &&
            (() => {
              const svc = services.find((s) => s.id === serviceId);
              if (!svc || svc.durationMinutes < 1440) return null;
              const minNights = Math.round(svc.durationMinutes / 1440);
              const startDay = new Date(checkInDate);
              startDay.setHours(0, 0, 0, 0);
              const endDay = new Date(checkOutDate);
              endDay.setHours(0, 0, 0, 0);
              const nights = Math.round((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000));
              if (nights < minNights) {
                return (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                    <p className="text-sm text-destructive">
                      Minimum stay for this property is {minNights} night{minNights !== 1 ? "s" : ""}. Your selection is {nights} night
                      {nights !== 1 ? "s" : ""}. Please choose a longer stay.
                    </p>
                  </div>
                );
              }
              return null;
            })()}
        </>
      ) : (
        <>
          <div className="h-[330px]">
            <label className="block text-sm font-medium text-foreground mb-2">Date *</label>
            <BookingDateCalendar
              value={date}
              onChange={(d) => {
                setDate(d);
                setSelectedTime("");
              }}
              minDate={minDate}
            />
          </div>

          {date && serviceId && (
            <div className="mt-2">
              <label className="block text-sm font-medium mb-1">Available Times *</label>
              {loadingSlots ? (
                <div className="text-sm text-muted-foreground py-2">Loading available slots...</div>
              ) : (() => {
                const filteredDisplaySlots = filterPastDisplaySlots(displaySlots);
                const filteredAvailableSlots = filterPastTimes(availableSlots);
                return filteredDisplaySlots.length === 0 && filteredAvailableSlots.length === 0 ? (
                  <div className="text-sm text-destructive py-2">
                    No available slots for this date. Please select another date.
                  </div>
                ) : filteredDisplaySlots.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {filteredDisplaySlots.map((ds) => (
                      <button
                        key={ds.time}
                        type="button"
                        onClick={() => {
                          setSelectedTime(ds.firstAvailableAt);
                          setMessage("");
                          setStatus("idle");
                        }}
                        className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                          selectedTime === ds.firstAvailableAt
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background hover:bg-muted border-input"
                        }`}
                      >
                        {ds.time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {filteredAvailableSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => {
                          setSelectedTime(time);
                          setMessage("");
                          setStatus("idle");
                        }}
                        className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                          selectedTime === time
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background hover:bg-muted border-input"
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                );
              })()}
              {selectedTime && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Selected: {selectedTime}
                  {selectedService && ` (Duration: ${selectedService.durationMinutes} min)`}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Type-Specific Fields (non-service-flow, e.g. old behaviour for restaurants etc.) */}
      {featureFlags.requiresStaffSelection && !isServiceFlow && (
        <StaffSelector
          staff={staff}
          value={staffId}
          onChange={setStaffId}
          required={true}
          showAnyOption={false}
        />
      )}

      {featureFlags.requiresIntakeForm && (
        <IntakeForm
          value={intakeData}
          onChange={(data) => setIntakeData(data)}
          required={true}
        />
      )}

      {featureFlags.allowsGroupBookings && businessType === "RESTAURANT" && (
        <PartySizeField
          value={partySize}
          onChange={setPartySize}
          required={true}
        />
      )}

      {businessType === "RESTAURANT" && (
        <DietaryRestrictionsField
          value={dietaryRestrictions}
          onChange={setDietaryRestrictions}
        />
      )}

      {/* Customer Fields */}
      <p className="text-sm text-muted-foreground mb-2">We need at least one way to reach you.</p>
      <div>
        <label className="block text-sm font-medium mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
        <p className="mt-1 text-xs text-muted-foreground">For your booking confirmation.</p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Phone number</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="e.g. 07xxx xxxxxx"
          className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
        <p className="mt-1 text-xs text-muted-foreground">For reminders and day-of contact.</p>
      </div>

      {businessType === "DRIVING_INSTRUCTOR" && publicPackagePurchases.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-1">Pay with lesson package</label>
          <select
            value={selectedPublicPackageId}
            onChange={(e) => setSelectedPublicPackageId(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Pay normally</option>
            {publicPackagePurchases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.lessonPackageName} ({p.lessonsRemaining} left)
              </option>
            ))}
          </select>
        </div>
      )}

      {customerProfile ? (
        <p className="text-sm text-muted-foreground">
          Signed in as {customerProfile.email}. Your details are saved for next time.{" "}
          Not you?{" "}
          <button type="button" onClick={() => { clearCustomerToken(); clearToken(); setCustomerProfile(null); router.refresh(); }} className="text-primary hover:underline">Log out</button>
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          <button type="button" onClick={() => goToCustomerAuth("/auth/customer/login")} className="text-primary hover:underline">Sign in</button>
          {" or "}
          <button type="button" onClick={() => goToCustomerAuth("/auth/customer/signup")} className="text-primary hover:underline">create an account</button>
          {" to save your details for next time."}
        </p>
      )}

      {businessType === "DRIVING_INSTRUCTOR" && (
        <>
          <div>
            <label className="block text-sm font-medium mb-1">Pickup address</label>
            <input
              type="text"
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="Where should we pick you up?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Drop-off address (optional)</label>
            <input
              type="text"
              value={dropoffAddress}
              onChange={(e) => setDropoffAddress(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="Where should we drop you off?"
            />
          </div>
        </>
      )}

      <div>
        <label className="block text-sm font-medium mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
          placeholder="Any additional notes or special requests"
        />
      </div>

      {message && (
        <div
          className={`rounded-md p-3 text-sm ${status === "success" ? "bg-green-50 text-green-800 dark:bg-green-900 dark:text-green-200" : status === "error" ? "bg-destructive/10 text-destructive" : status === "loading" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"} ${status === "error" ? "whitespace-pre-line" : ""}`}
        >
          {status === "loading" ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              <span>{message}</span>
            </div>
          ) : (
            message
          )}
          {status === "success" && payLaterLink && (
            <div className="mt-3">
              <Link
                href={payLaterLink}
                className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-95 transition-opacity"
                {...fsElement("Booking Success Pay Now Link", { id: "booking-success-pay-now", section: "booking-success" })}
              >
                Pay now
              </Link>
            </div>
          )}
          {status === "error" && suggestedRanges.length > 0 && (
            <div className="mt-3">
              <p className="font-medium text-foreground mb-2">Available alternatives for the same length of stay:</p>
              <ul className="space-y-1.5">
                {suggestedRanges.map((r) => (
                  <li key={`${r.startDate}-${r.endDate}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setCheckInDate(r.startDate);
                        setCheckOutDate(r.endDate);
                        setSuggestedRanges([]);
                        setMessage("");
                        setStatus("idle");
                      }}
                      className="text-left w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      {r.startDate} to {r.endDate}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <button
          type="submit"
          disabled={
            status === "loading" ||
            (isPropertyRental
              ? !checkInDate ||
              !checkOutDate ||
              new Date(checkOutDate) <= new Date(checkInDate) ||
              propertyRentalNightsTooShort ||
              propertyRentalRangeOverlapsBlocked
              : !selectedTime)
          }
          className="w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          {...fsElement(
            isPropertyRental ? "Booking Submit Request Button" : "Booking Confirm Button",
            { id: "booking-submit", section: "booking-form" }
          )}
        >
          {status === "loading" ? "Booking…" : isPropertyRental ? "Submit booking request" : acceptCashPayments && selectedService && selectedService.priceCents > 0 && !acceptsPayments ? "Confirm booking · Pay at appointment" : "Confirm booking"}
        </button>
        {acceptsPayments && acceptCashPayments && selectedService && selectedService.priceCents > 0 && (
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={(e) => {
                submitPayWithCashRef.current = true;
                (e.target as HTMLButtonElement).closest("form")?.requestSubmit();
              }}
              disabled={
                status === "loading" ||
                (isPropertyRental
                  ? !checkInDate || !checkOutDate || new Date(checkOutDate) <= new Date(checkInDate) || propertyRentalNightsTooShort || propertyRentalRangeOverlapsBlocked
                  : !selectedTime)
              }
              className="w-full rounded-lg border border-primary bg-transparent py-3 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
              {...fsElement("Booking Pay When I Arrive Button", { id: "booking-pay-cash", section: "booking-form" })}
            >
              Pay when I arrive
            </button>
            <p className="text-xs text-muted-foreground text-center">
              No payment now, pay by cash at your appointment
            </p>
          </div>
        )}
      </div>
    </form>
  );
}

// ==========================================
// Step Indicator Component
// ==========================================
type StepDef = { key: BookingStep; label: string };

function getStepsForBusiness(hasMultipleLocations: boolean): StepDef[] {
  const steps: StepDef[] = [{ key: "service", label: "Service" }];
  if (hasMultipleLocations) {
    steps.push({ key: "location", label: "Location" });
  }
  steps.push(
    { key: "professional", label: "Professional" },
    { key: "datetime", label: "Date & Time" },
    { key: "details", label: "Confirm" }
  );
  return steps;
}

function StepIndicator({ steps, currentStep }: { steps: StepDef[]; currentStep: BookingStep }) {
  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="flex w-full mb-2">
      {steps.map((step, i) => {
        const isActive = i === currentIndex;
        const isCompleted = i < currentIndex;

        return (
          <div key={step.key} className="flex flex-1 flex-col items-center min-w-0">
            {/* Circle row with connectors - aligned in one horizontal flow */}
            <div className="flex items-center w-full">
              <div
                className={`h-0.5 flex-1 min-h-0 ${
                  i > 0 ? (i <= currentIndex ? "bg-primary/30" : "bg-muted") : "bg-transparent"
                }`}
                aria-hidden
              />
              <div
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : isCompleted
                    ? "bg-primary/20 text-primary"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {isCompleted ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <div
                className={`h-0.5 flex-1 min-h-0 ${
                  i < steps.length - 1 ? (isCompleted ? "bg-primary/30" : "bg-muted") : "bg-transparent"
                }`}
                aria-hidden
              />
            </div>
            {/* Label centered under circle */}
            <span
              className={`mt-1.5 text-[10px] leading-tight text-center ${isActive ? "text-primary font-medium" : "text-muted-foreground"}`}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
